import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { getActions } from "./api"
import { allowUpdate } from "../../app/store"

const initialState = {
  // this list won't encompass all types but is useful for the known types
  types: {
    login: {
      name: "login",
      colorCode: "#ffd8ad",
    },
    logout: {
      name: "logout",
      colorCode: "#a8c6fa",
    },
    deploy: {
      name: "deployed astrosite",
      colorCode: "#cbe8ba",
    },
    send_udl: {
      name: "sent to UDL",
      colorCode: "#f6a2bf",
    },
  },
  byId: {}, // A map of data submissions by id
  matchesByCtx: {
    // root context powering the actions list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // actions by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "observation/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
}

/**
 * Thunk to perform async logic to fetch the action logs matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchActions = createAsyncThunk(
  "actions/fetch",
  /**
   *
   * @param filter the filters to apply to the search
   * @param page Pagination information
   * @param context The context from where the query is being made.  If its from the data submissions list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getActions({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Audit Actions reducer
 */
export const actionsSlice = createSlice({
  name: "actions",
  initialState,
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchActions.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchActions.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((act) => {
              // Add the action to state by id (this can include ids not retrieved in this call)
              state.byId[act.id] = {
                ...act,
                _at: action.payload.at,
              }
              // add the ids that match from this query
              if (state.matchesByCtx[context].ids.indexOf(act.id) === -1) {
                state.matchesByCtx[context].ids.push(act.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchActions.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
  },
})

// Selector for all submissions
export const selectActionsById = (state) => state.actions.byId
// Selector for the known types of actions
export const selectActionTypes = (state) => state.actions.types

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectActionsByContext(state, `rso/${id}`))
 */
export const selectActionsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.actions.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`submissions, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectActionsByIds(state, ["1", "2"]))
 */
export const selectActionsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectActionsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export default actionsSlice.reducer
